// THE OPERATIONS INBOX'S UNANSWERED CLIENT MESSAGES CARD, AS A PURE READING:
// what it owes, from what the Client Inbox itself reads. No database:
// lib/server/ops-inbox-comms.ts reads the facts, and the tick hands each
// card's reading to `stepOwed` (lib/ops-inbox-cards.ts). The action-item
// cards are lib/ops-inbox-items.ts'.
//
// ⚠️ THE PAGE'S OWN SIGNAL, NEVER A RULE OF OUR OWN. A message is waiting when
// the Client Inbox's `decideWait` says so (its Slack waits only: a meeting
// follow-up is reviewed through its action item).
//
// The card goes late: it is due on its oldest waiting message's day.
//
// Shared, not server-only, and erasable TypeScript: the tick runs in a sidecar
// under node's type stripping.

import type { InboxSent, InboxWait } from './client-slack-inbox';
import type { CardRow, OwedReading } from './ops-inbox-cards';
import { formatShort } from './schedule-dates';
import { operationsDay } from './throughput-operations';

const plural = (n: number, word: string): string => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;

/** A list of strings back from `detail_json`, the non-strings dropped. */
const stringsOf = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

// ─── Unanswered client messages ─────────────────────────────────────────────

/**
 * HOW A WAIT STOPPED WAITING, where the inbox recorded it: a reply or reaction
 * sent from the app, an answer in Slack itself, a person's mark, or (with no
 * time and no one: Slack keeps none for a reaction) a reaction or the model's
 * settled read.
 */
export type WaitCloseHow = 'reply' | 'react' | 'answered' | 'no_reply' | 'answered_elsewhere' | 'archived' | 'acknowledged' | 'settled';

export interface WaitClose {
  how: WaitCloseHow;
  /** The person's email, when one is known. */
  by: string | null;
  /** Who answered in Slack, by their Slack name: for an answer whose author has no login we know. */
  name: string | null;
  /** When (ms), where anyone recorded it. */
  at: number | null;
}

/** One of a brand's Slack waits, as the card reads it. */
export interface BrandWait {
  id: string;
  /** The first waiting message, in Slack's seconds. */
  start: number;
  waiting: boolean;
  /** How it stopped waiting; null while it waits, and for one never owed a reply. */
  close: WaitClose | null;
}

/**
 * HOW ONE WAIT STOPPED WAITING, from the inbox's own decision (`decideWait`):
 * a person's mark, else an answer (sent from the app, else written in Slack),
 * else a reaction or a settled read. Null while it waits, and for a wait that
 * never needed a reply. `sent` is the app's latest send for it; `email` the
 * login of whoever answered in Slack, where the roster knows their Slack.
 */
export function waitClose(
  w: Pick<InboxWait, 'end' | 'decision' | 'answeredBy'>,
  sent: Pick<InboxSent, 'kind' | 'by' | 'at'> | undefined,
  email: string | null,
): WaitClose | null {
  const d = w.decision;
  if (d.waiting) return null;
  const m = d.mark;
  if (m !== null && (m.state === 'no_reply' || (m.state === 'answered_elsewhere' && d.elsewhere) || (m.state === 'archived' && d.archived))) {
    return { how: m.state, by: m.by, name: null, at: m.at };
  }
  if (w.end !== null) {
    if (!d.needs) return null;
    if (sent !== undefined) return { how: sent.kind, by: sent.by, name: null, at: sent.at };
    // Slack's time is fractional seconds; a card's times are whole ms.
    return { how: 'answered', by: email, name: w.answeredBy ?? null, at: Math.round(w.end * 1000) };
  }
  if (d.acknowledged) return sent?.kind === 'react' ? { how: 'react', by: sent.by, name: null, at: sent.at } : { how: 'acknowledged', by: null, name: null, at: null };
  if (d.settled !== null) return { how: 'settled', by: null, name: null, at: null };
  return null;
}

/** Why a messages card closed for good, kept on it (`detail_json.closed`): a wait's close, or nothing waiting any more. */
export type MessagesClosed = WaitCloseHow | 'gone';

/**
 * WHY A MESSAGES CARD IS DONE, in words: "Replied in Slack by Dana". `who` is
 * the person's name as the line says it, null when nobody is known.
 */
export function messagesClosedWords(how: MessagesClosed, who: string | null): string {
  const by = who ? ` by ${who}` : '';
  switch (how) {
    case 'reply': return `Replied in Slack${by}`;
    case 'react': return `Reacted in Slack${by}`;
    case 'answered': return `Answered in Slack${by}`;
    case 'no_reply': return `Marked no reply needed${by}`;
    case 'answered_elsewhere': return `Marked answered elsewhere${by}`;
    case 'archived': return `Archived in the Client Inbox${by}`;
    case 'acknowledged': return 'Acknowledged with a reaction in Slack';
    case 'settled': return 'The model read it as settled without a reply';
    case 'gone': return 'Nothing waiting any more';
  }
}

const MESSAGES_CLOSED: readonly MessagesClosed[] = ['reply', 'react', 'answered', 'no_reply', 'answered_elsewhere', 'archived', 'acknowledged', 'settled', 'gone'];
/** A messages card's `detail_json.closed` read back: a known close, else "gone". */
export const messagesClosedOf = (v: unknown): MessagesClosed => (MESSAGES_CLOSED as readonly unknown[]).includes(v) ? (v as MessagesClosed) : 'gone';

/** Whether a close's words carry a time worth saying ("at 3:12pm"): the ones somebody did at a known moment. */
export const closeHasTime = (how: MessagesClosed): boolean => how !== 'acknowledged' && how !== 'settled' && how !== 'gone';

const lower = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);

/**
 * AN UNANSWERED CLIENT MESSAGES CARD'S READING (`stepOwed`'s), for one brand.
 * Owed: its waiting Slack messages, as the Client Inbox lists them. Due on the
 * oldest one's day, so it goes late. Its counter is the day's work:
 * those answered today (by a close with a time) of those plus the ones still
 * waiting. Done when nothing waits, explained by how the last of the card's
 * waits stopped waiting ("Replied in Slack by Dana").
 *
 *   waits             the waiting messages' ids (the inbox's wait ids)
 *   closed / closedOn / closedName   why it closed for good, the day, and a
 *                     Slack name when nobody we know answered
 *
 * `nameOf` gives a person's first name for the history line.
 */
export function clientMessagesReading(card: CardRow | null, waits: readonly BrandWait[], today: string, at: number, nameOf: (email: string) => string): OwedReading {
  const waiting = waits.filter((w) => w.waiting).sort((a, b) => a.start - b.start || (a.id < b.id ? -1 : 1));
  const doneToday = waits.filter((w) => w.close?.at != null && operationsDay(w.close.at) === today).length;
  const owed = waiting.length;
  const oldest = waiting[0];
  const dueOn = oldest === undefined ? (card?.dueOn ?? today) : operationsDay(oldest.start * 1000);
  const workSince = waiting.reduce<number | null>((m, w) => (m === null || w.start * 1000 > m ? w.start * 1000 : m), null);
  if (owed === 0 && card !== null) {
    // A card already closed keeps its why: a later pass must not move it to another wait's close.
    const kept = card.state === 'done' && typeof card.detail?.closed === 'string' ? card.detail : null;
    const listed = new Set(stringsOf(card.detail?.waits));
    const closes = waits.filter((w) => w.close !== null && listed.has(w.id)).map((w) => w.close as WaitClose);
    // The newest close with a time; else one with none (a reaction, a settled read); else nothing to say.
    const last = closes.filter((c) => c.at !== null).sort((a, b) => (b.at ?? 0) - (a.at ?? 0))[0] ?? closes[0] ?? null;
    const how: MessagesClosed = last?.how ?? 'gone';
    const who = last === null ? null : last.by !== null ? nameOf(last.by) : last.name;
    const closedOn = kept !== null && typeof kept.closedOn === 'string' ? kept.closedOn : operationsDay(last?.at ?? at);
    return {
      owed: 0,
      dueOn,
      // Closed, its counter is the day it closed: the next day's empty count would rewrite it for nothing.
      target: kept !== null ? card.target : doneToday,
      doneN: kept !== null ? card.doneN : doneToday,
      done: last !== null && last.at !== null ? { actor: last.by, at: last.at, via: 'app' } : null,
      workSince: null,
      extra: kept !== null
        ? { waits: [], closed: kept.closed, closedOn, closedName: kept.closedName ?? null }
        : { waits: [], closed: how, closedOn, closedName: last !== null && last.by === null ? last.name : null },
      notes: { closed: `closed: ${lower(messagesClosedWords(how, who))}` },
    };
  }
  return {
    owed,
    dueOn,
    target: owed + doneToday,
    doneN: doneToday,
    done: null,
    workSince,
    extra: { waits: waiting.map((w) => w.id) },
    notes: {
      made: oldest === undefined ? undefined : `opened: ${plural(owed, 'message')} waiting, the oldest since ${formatShort(dueOn)}`,
      reopened: `reopened: ${plural(owed, 'message')} waiting`,
      back: `${plural(owed, 'message')} waiting`,
    },
  };
}
